/**
 * MarketingMapExport — matches MAP.png / MAP 2.png reference layouts
 *
 * Layout anatomy (1200 × 900 px canvas):
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │  HEADER BAR  — gold bold address | city | neighborhood               │
 *   ├─────────────────────────────────────┬────────────────────────────────┤
 *   │  DRIVE-TIME CHIPS ROW               │                                │
 *   ├─────────────────────────────────────┤    RIGHT PANEL                 │
 *   │                                     │    "NEARBY AMENITIES" list     │
 *   │   MAP AREA  (static tile bg)        │    with category icons         │
 *   │   · floating POI callout boxes      │                                │
 *   │   · gold ⭐ property pin            ├────────────────────────────────┤
 *   │                                     │    DRIVE TIMES TABLE           │
 *   ├────────────────────┬────────────────┴────────────────────────────────┤
 *   │ Community logo     │  Disclaimer + branding                          │
 *   └────────────────────┴─────────────────────────────────────────────────┘
 *
 * Export strategy: render this div, call html2canvas on it (no Leaflet tiles inside canvas → no CORS).
 * The map background is a static-tile <img> (Geoapify or OSM Static, loaded as same-origin via fetch proxy or CORS-enabled CDN).
 */

import { useRef, useState, useEffect } from 'react';
import { Download, Printer, X, Loader, Map } from 'lucide-react';
import { Amenity, NearbyResult, getCategoryConfig, haversineDistanceMiles } from '@/lib/index';

// ─── Brand ────────────────────────────────────────────────────────────────────
const B = {
  darkNavy: '#001A3A',
  navy: '#002060',
  gold: '#C9A84C',
  goldDark: '#8B6914',
  red: '#CC0000',
  white: '#FFFFFF',
  offWhite: '#F8F6F2',
  lightBlue: '#E8F0F8',
  border: '#D0D8E8',
};

// ─── Drive-time landmarks (Charlotte NC context) ───────────────────────────────
const DRIVE_TIME_LANDMARKS = [
  { label: 'Uptown Charlotte', approxMiles: 15, approxMin: 22 },
  { label: 'Charlotte Douglas Int\'l Airport', approxMiles: 20, approxMin: 28 },
  { label: 'I-485 Access', approxMiles: 3, approxMin: 8 },
  { label: 'South Park Mall', approxMiles: 8, approxMin: 14 },
  { label: 'Ballantyne', approxMiles: 5, approxMin: 10 },
];

// ─── Compute rough drive times from listing distance (simple heuristic) ─────
function estimateDriveMins(miles: number): number {
  // Assume 25 mph average in suburban Charlotte
  return Math.max(1, Math.round((miles / 25) * 60));
}

// ─── POI callout box positions on the map canvas ─────────────────────────────
// Map area is left 820px, height 600px (after header + chips)
// Positions are % of map area
type BoxPos = 'TL' | 'TR' | 'BL' | 'BR' | 'TC' | 'BC';
const CALLOUT_POSITIONS: BoxPos[] = ['TL', 'TR', 'BL', 'BR', 'TC', 'BC'];
const CALLOUT_STYLE: Record<BoxPos, React.CSSProperties> = {
  TL: { top: '8%', left: '2%' },
  TR: { top: '8%', right: '2%' },
  BL: { bottom: '10%', left: '2%' },
  BR: { bottom: '10%', right: '2%' },
  TC: { top: '4%', left: '50%', transform: 'translateX(-50%)' },
  BC: { bottom: '4%', left: '50%', transform: 'translateX(-50%)' },
};

// ─── Category icon map (emoji fallbacks — reliable in html2canvas) ────────────
const CAT_ICON: Record<string, string> = {
  grocery: '🛒',
  shopping: '🛍️',
  restaurant: '🍽️',
  school: '🎓',
  hospital: '🏥',
  park: '🌳',
  highway: '🛣️',
  fitness: '💪',
  coffee: '☕',
  pharmacy: '💊',
  gas: '⛽',
  default: '📍',
};

function catIcon(cat: string): string {
  return CAT_ICON[cat] ?? CAT_ICON.default;
}

// ─── Group amenities into callout clusters ────────────────────────────────────
interface CalloutGroup {
  pos: BoxPos;
  label: string;
  items: NearbyResult[];
  emoji: string;
}

function buildCalloutGroups(amenities: NearbyResult[]): CalloutGroup[] {
  // Group by category, max 6 groups
  const byCategory: Record<string, NearbyResult[]> = {};
  for (const a of amenities) {
    if (!byCategory[a.category]) byCategory[a.category] = [];
    if (byCategory[a.category].length < 3) byCategory[a.category].push(a);
  }
  const cats = Object.keys(byCategory).slice(0, 6);
  return cats.map((cat, i) => ({
    pos: CALLOUT_POSITIONS[i] as BoxPos,
    label: cat.charAt(0).toUpperCase() + cat.slice(1),
    items: byCategory[cat],
    emoji: catIcon(cat),
  }));
}

// ─── Right-panel amenity list (top items, sorted by distance) ─────────────────
function buildRightPanelItems(amenities: NearbyResult[], listing: Amenity): NearbyResult[] {
  return [...amenities]
    .sort((a, b) => {
      const da = haversineDistanceMiles(listing.lat, listing.lng, a.lat, a.lng);
      const db = haversineDistanceMiles(listing.lat, listing.lng, b.lat, b.lng);
      return da - db;
    })
    .slice(0, 8);
}

// ─── Static map tile URL (no API key, CORS-enabled) ──────────────────────────
function buildStaticMapUrl(lat: number, lng: number, style: 'aerial' | 'street'): string {
  // We use the Leaflet tile approach: render a static image from a CORS-friendly tile server
  // For html2canvas: we load map as <img> crossOrigin="anonymous" from a CORS tile URL
  // CartoDB tiles support CORS — use a tile URL pointing to zoom/x/y
  if (style === 'street') {
    // CartoDB Voyager — clean street map
    return `https://a.basemaps.cartocdn.com/rastertiles/voyager/${encodeMapTile(lat, lng, 13)}`;
  } else {
    // CartoDB Dark Matter — gives the dark-background aerial feel without satellite imagery (no CORS issues)
    return `https://a.basemaps.cartocdn.com/dark_matter_nolabels/${encodeMapTile(lat, lng, 13)}`;
  }
}

// Convert lat/lng to tile x/y/z string
function encodeMapTile(lat: number, lng: number, zoom: number): string {
  const n = Math.pow(2, zoom);
  const x = Math.floor((lng + 180) / 360 * n);
  const latRad = lat * Math.PI / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return `${zoom}/${x}/${y}.png`;
}

// ─── Build static map via Geoapify (free tier, no CORS issues as <img>) ───────
function buildGeoapifyUrl(lat: number, lng: number, style: 'aerial' | 'street'): string {
  const mapStyle = style === 'street' ? 'osm-bright' : 'dark-matter-brown';
  const w = 820, h = 600;
  // Use Geoapify's free tier marker pin
  const marker = `marker=lonlat:${lng},${lat};type:star;color:%23C9A84C;size:x-large`;
  // NOTE: We use the static-map API which serves a proper PNG with real coordinate center
  // Free tier = 3000 req/day; no API key for basic usage via public endpoint
  return (
    `https://maps.geoapify.com/v1/staticmap?style=${mapStyle}` +
    `&width=${w}&height=${h}&center=lonlat:${lng},${lat}&zoom=13` +
    `&${marker}&apiKey=NO_KEY_NEEDED_FOR_PREVIEW`
  );
}

// ─── Fallback: OpenStreetMap Static via osm.org embed URL ────────────────────
function buildOSMUrl(lat: number, lng: number): string {
  const delta = 0.04;
  return `https://www.openstreetmap.org/export/embed.html?bbox=${lng - delta},${lat - delta},${lng + delta},${lat + delta}&layer=mapnik&marker=${lat},${lng}`;
}

// ─── Component Props ──────────────────────────────────────────────────────────
interface MarketingMapExportProps {
  listing: Amenity;
  amenities: NearbyResult[];
  onClose: () => void;
  /** Optional neighborhood/community name to show in header */
  communityName?: string;
  /** Optional city text shown in header after address */
  cityText?: string;
}

// ─── Callout Box Component ────────────────────────────────────────────────────
function CalloutBox({ group, listing }: { group: CalloutGroup; listing: Amenity }) {
  const firstItem = group.items[0];
  const dist = firstItem
    ? haversineDistanceMiles(listing.lat, listing.lng, firstItem.lat, firstItem.lng).toFixed(1)
    : '—';
  const driveMin = firstItem ? estimateDriveMins(parseFloat(dist)) : 0;
  const cfg = getCategoryConfig(group.items[0]?.category ?? 'default');

  return (
    <div style={{
      position: 'absolute',
      ...CALLOUT_STYLE[group.pos],
      background: 'rgba(255,255,255,0.97)',
      border: `2px solid ${B.darkNavy}`,
      borderRadius: '10px',
      padding: '8px 12px',
      minWidth: '160px',
      maxWidth: '200px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
      zIndex: 10,
    }}>
      {/* Category header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        marginBottom: '4px',
      }}>
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%',
          background: cfg.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '14px', flexShrink: 0,
        }}>
          {group.emoji}
        </div>
        <span style={{
          fontFamily: 'Arial, sans-serif', fontWeight: '700',
          fontSize: '11px', color: B.darkNavy, lineHeight: 1.2,
        }}>
          {group.label}
        </span>
      </div>

      {/* Items */}
      {group.items.slice(0, 2).map((item, i) => {
        const d = haversineDistanceMiles(listing.lat, listing.lng, item.lat, item.lng).toFixed(1);
        const m = estimateDriveMins(parseFloat(d));
        return (
          <div key={i} style={{
            borderTop: i === 0 ? `1px solid ${B.border}` : 'none',
            paddingTop: i === 0 ? '5px' : '0',
            paddingBottom: '3px',
          }}>
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px', fontWeight: '600', color: B.darkNavy, lineHeight: 1.3 }}>
              {item.name}
            </div>
            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px', color: '#555' }}>
              ~{d} mi · {m} min drive
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Right Panel Item ──────────────────────────────────────────────────────────
function RightPanelItem({ item, listing }: { item: NearbyResult; listing: Amenity }) {
  const dist = haversineDistanceMiles(listing.lat, listing.lng, item.lat, item.lng).toFixed(1);
  const mins = estimateDriveMins(parseFloat(dist));
  const cfg = getCategoryConfig(item.category);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '6px 0',
      borderBottom: `1px solid ${B.border}`,
    }}>
      <div style={{
        width: '30px', height: '30px', borderRadius: '6px',
        background: cfg.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '14px', flexShrink: 0,
      }}>
        {catIcon(item.category)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Arial, sans-serif', fontSize: '11px',
          fontWeight: '700', color: B.darkNavy,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {item.name}
        </div>
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px', color: '#666' }}>
          {dist} mi · {mins} min
        </div>
      </div>
    </div>
  );
}

// ─── Drive Time Row ────────────────────────────────────────────────────────────
function DriveTimes({ listing }: { listing: Amenity }) {
  // Compute approximate distances from listing to Charlotte key landmarks
  // We use haversine to key lat/lng pairs
  const landmarks = [
    { label: 'Uptown Charlotte', lat: 35.2271, lng: -80.8431 },
    { label: 'I-485 Access', lat: listing.lat + 0.03, lng: listing.lng + 0.02 },
    { label: 'Charlotte Douglas', lat: 35.2144, lng: -80.9473 },
  ];

  return (
    <div style={{
      background: B.darkNavy,
      borderRadius: '10px',
      padding: '12px 14px',
      marginTop: '10px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: '10px',
      }}>
        <span style={{ fontSize: '16px' }}>🚗</span>
        <span style={{
          fontFamily: 'Arial, sans-serif', fontWeight: '800',
          fontSize: '12px', color: B.gold, letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}>
          Drive Times
        </span>
      </div>

      {landmarks.map((lm, i) => {
        const miles = haversineDistanceMiles(listing.lat, listing.lng, lm.lat, lm.lng);
        const mins = estimateDriveMins(miles);
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '5px 0',
            borderBottom: i < landmarks.length - 1 ? '1px solid rgba(255,255,255,0.12)' : 'none',
          }}>
            <span style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px', color: B.white }}>
              {lm.label}
            </span>
            <span style={{
              fontFamily: 'Arial, sans-serif', fontSize: '10px',
              color: 'rgba(255,255,255,0.7)', textAlign: 'right',
            }}>
              ~{miles.toFixed(1)} mi · {mins} min
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Drive Chips Row ──────────────────────────────────────────────────────────
function DriveChips({ listing }: { listing: Amenity }) {
  const chips = [
    { label: 'Downtown', lat: 35.2271, lng: -80.8431, icon: '🏙️' },
    { label: 'I-485', lat: listing.lat + 0.03, lng: listing.lng + 0.02, icon: '🛣️' },
    { label: 'Airport', lat: 35.2144, lng: -80.9473, icon: '✈️' },
  ];
  return (
    <div style={{
      display: 'flex', gap: '12px', padding: '10px 14px',
      background: 'rgba(255,255,255,0.96)',
      borderBottom: `2px solid ${B.border}`,
    }}>
      {chips.map((c, i) => {
        const miles = haversineDistanceMiles(listing.lat, listing.lng, c.lat, c.lng);
        const mins = estimateDriveMins(miles);
        return (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '6px 14px',
            border: `1px solid ${B.border}`,
            borderRadius: '24px',
            background: B.white,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}>
            <span style={{ fontSize: '18px' }}>{c.icon}</span>
            <div>
              <div style={{ fontFamily: 'Arial, sans-serif', fontWeight: '700', fontSize: '11px', color: B.darkNavy }}>
                {c.label}
              </div>
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px', color: '#666' }}>
                ~{miles.toFixed(1)} mi · {mins} min
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Export Component ────────────────────────────────────────────────────
export function MarketingMapExport({ listing, amenities, onClose, communityName, cityText }: MarketingMapExportProps) {
  const exportRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [mapStyle, setMapStyle] = useState<'street' | 'dark'>('street');
  const [mapImgLoaded, setMapImgLoaded] = useState(false);
  const [mapImgError, setMapImgError] = useState(false);

  const calloutGroups = buildCalloutGroups(amenities);
  const rightPanelItems = buildRightPanelItems(amenities, listing);

  // Parse address parts
  const addrParts = listing.address.split(',');
  const streetAddr = addrParts[0]?.trim() ?? listing.address;
  const cityState = addrParts.slice(1, 3).join(',').trim();
  const displayCity = cityText ?? cityState;
  const displayCommunity = communityName ?? listing.name ?? '';

  // Static map URL — use a tile stitching approach
  // We'll use a multi-tile approach: fetch a 3×2 grid of tiles and stitch them
  // For CORS: CartoDB tiles are CORS-enabled
  const zoom = 14;
  const n = Math.pow(2, zoom);
  const tileX = Math.floor((listing.lng + 180) / 360 * n);
  const latRad = listing.lat * Math.PI / 180;
  const tileY = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);

  // Build a 3×3 tile grid centered on property (each tile = 256px → 768×768 total)
  const tileBase = mapStyle === 'street'
    ? 'https://a.basemaps.cartocdn.com/rastertiles/voyager'
    : 'https://a.basemaps.cartocdn.com/dark_matter';

  const tiles: { x: number; y: number; dx: number; dy: number }[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      tiles.push({ x: tileX + dx, y: tileY + dy, dx, dy });
    }
  }

  // For html2canvas: we render the map as a grid of <img> elements with crossOrigin
  // Each 256×256 tile
  const TILE_SIZE = 256;

  // Map area dimensions
  const MAP_W = 820;
  const MAP_H = 560;

  // Download function
  const handleDownload = async (format: 'png' | 'jpg') => {
    if (!exportRef.current || downloading) return;
    setDownloading(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      // Wait a bit for tiles to be painted
      await new Promise(r => setTimeout(r, 1200));
      const canvas = await html2canvas(exportRef.current, {
        useCORS: true,
        allowTaint: false,
        scale: 2,
        logging: false,
        backgroundColor: '#ffffff',
        width: 1200,
        height: 900,
        windowWidth: 1200,
      });

      const mimeType = format === 'jpg' ? 'image/jpeg' : 'image/png';
      const quality = format === 'jpg' ? 0.95 : undefined;
      const dataUrl = format === 'jpg'
        ? canvas.toDataURL(mimeType, quality)
        : canvas.toDataURL(mimeType);

      const filename = `${streetAddr.replace(/[^a-z0-9]/gi, '-')}-driven-map.${format}`;
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = filename;
      a.click();
    } catch (err) {
      console.error('Export failed', err);
      alert('Export failed — try Print / Save as PDF instead.');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <div className="fixed inset-0 z-[70] flex flex-col items-center justify-start overflow-auto py-4 px-2"
      style={{ background: 'rgba(0,0,0,0.88)' }}>

      {/* ── Toolbar ── */}
      <div className="no-print flex items-center gap-2 mb-4 flex-wrap justify-center">
        {/* Style toggle */}
        <div className="flex rounded-full overflow-hidden border-2 border-white/30">
          <button
            onClick={() => setMapStyle('street')}
            className="px-3 py-1.5 text-xs font-bold transition-all"
            style={{ background: mapStyle === 'street' ? B.gold : 'transparent', color: B.white }}>
            🗺️ Street
          </button>
          <button
            onClick={() => setMapStyle('dark')}
            className="px-3 py-1.5 text-xs font-bold transition-all"
            style={{ background: mapStyle === 'dark' ? B.darkNavy : 'transparent', color: B.white, borderLeft: '2px solid rgba(255,255,255,0.3)' }}>
            🌑 Dark
          </button>
        </div>

        <button
          onClick={() => handleDownload('png')}
          disabled={downloading}
          className="flex items-center gap-1.5 text-white text-sm font-bold px-4 py-2 rounded-full disabled:opacity-50 transition-all"
          style={{ background: B.darkNavy }}>
          {downloading ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {downloading ? 'Generating…' : 'PNG'}
        </button>

        <button
          onClick={() => handleDownload('jpg')}
          disabled={downloading}
          className="flex items-center gap-1.5 text-white text-sm font-bold px-4 py-2 rounded-full disabled:opacity-50 transition-all"
          style={{ background: B.navy }}>
          {downloading ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          JPG
        </button>

        <button
          onClick={handlePrint}
          className="flex items-center gap-1.5 text-white text-sm font-bold px-4 py-2 rounded-full transition-all"
          style={{ background: B.red }}>
          <Printer className="w-4 h-4" /> PDF
        </button>

        <button
          onClick={onClose}
          className="flex items-center gap-1.5 bg-white/20 text-white text-sm font-bold px-4 py-2 rounded-full hover:bg-white/30 transition-all">
          <X className="w-4 h-4" /> Close
        </button>
      </div>

      {/* ── THE EXPORT CANVAS — 1200 × 900 ── */}
      <div
        ref={exportRef}
        style={{
          width: '1200px',
          minWidth: '1200px',
          height: '900px',
          background: B.white,
          borderRadius: '6px',
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Arial, Helvetica, sans-serif',
          position: 'relative',
        }}>

        {/* ━━━ HEADER BAR ━━━ */}
        <div style={{
          background: B.darkNavy,
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexShrink: 0,
        }}>
          <span style={{
            fontFamily: 'Arial Black, Arial, sans-serif',
            fontSize: '26px',
            fontWeight: '900',
            color: B.gold,
            letterSpacing: '-0.5px',
            lineHeight: 1,
          }}>
            {streetAddr}
          </span>
          {displayCity && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '20px' }}>|</span>
              <span style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '18px',
                fontWeight: '700',
                color: B.white,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}>
                {displayCity}
              </span>
            </>
          )}
          {displayCommunity && (
            <>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '20px' }}>|</span>
              <span style={{
                fontFamily: 'Arial, sans-serif',
                fontSize: '16px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.85)',
                letterSpacing: '0.02em',
              }}>
                {displayCommunity}
              </span>
            </>
          )}
        </div>

        {/* ━━━ DRIVE-TIME CHIPS ROW ━━━ */}
        <DriveChips listing={listing} />

        {/* ━━━ BODY: MAP + RIGHT PANEL ━━━ */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* ─ MAP AREA ─ */}
          <div style={{
            position: 'relative',
            width: `${MAP_W}px`,
            flexShrink: 0,
            overflow: 'hidden',
            background: mapStyle === 'dark' ? '#1a1a2e' : '#e8e0d8',
          }}>

            {/* Tile grid — 3×3 grid centered on property */}
            <div style={{
              position: 'absolute',
              // Center the grid: each tile is 256px, 3 tiles = 768px
              // Grid center = 768/2 = 384; map center = MAP_W/2 = 410 → offset = 410-384 = 26
              left: `${MAP_W / 2 - 384}px`,
              top: `${MAP_H / 2 - 384}px`,
              width: '768px',
              height: '768px',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 256px)',
              gridTemplateRows: 'repeat(3, 256px)',
            }}>
              {tiles.map((t, i) => (
                <img
                  key={i}
                  src={`${tileBase}/${zoom}/${t.x}/${t.y}.png`}
                  crossOrigin="anonymous"
                  width={TILE_SIZE}
                  height={TILE_SIZE}
                  style={{ display: 'block', width: `${TILE_SIZE}px`, height: `${TILE_SIZE}px` }}
                  alt=""
                />
              ))}
            </div>

            {/* Dark overlay for readability (only for dark style) */}
            {mapStyle === 'dark' && (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(0,0,20,0.3)',
                pointerEvents: 'none', zIndex: 2,
              }} />
            )}

            {/* Property PIN — gold star at exact center of map */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -100%)',
              zIndex: 20,
              textAlign: 'center',
            }}>
              {/* Gold star */}
              <div style={{
                fontSize: '36px',
                lineHeight: 1,
                filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))',
              }}>
                ⭐
              </div>
              {/* Address label */}
              <div style={{
                background: B.darkNavy,
                color: B.white,
                fontFamily: 'Arial, sans-serif',
                fontSize: '11px',
                fontWeight: '700',
                padding: '4px 10px',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                marginTop: '2px',
                border: `1px solid ${B.gold}`,
              }}>
                {streetAddr}
              </div>
            </div>

            {/* Callout boxes overlaid on map */}
            {calloutGroups.map((g, i) => (
              <CalloutBox key={i} group={g} listing={listing} />
            ))}

            {/* North arrow */}
            <div style={{
              position: 'absolute', bottom: '14px', left: '14px',
              background: 'rgba(255,255,255,0.95)',
              borderRadius: '50%', width: '32px', height: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Arial, sans-serif', fontSize: '12px', fontWeight: '900',
              color: B.darkNavy, boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 15,
            }}>
              N
            </div>
          </div>

          {/* ─ RIGHT PANEL ─ */}
          <div style={{
            width: '380px',
            flexShrink: 0,
            background: B.white,
            display: 'flex',
            flexDirection: 'column',
            padding: '0',
            overflow: 'hidden',
          }}>
            {/* Nearby Amenities header */}
            <div style={{
              background: B.darkNavy,
              padding: '10px 16px',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}>
              <span style={{ fontSize: '14px' }}>📍</span>
              <span style={{
                fontFamily: 'Arial, sans-serif', fontWeight: '800',
                fontSize: '12px', color: B.gold, letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}>
                Nearby Amenities
              </span>
            </div>

            {/* Amenity list */}
            <div style={{ flex: 1, padding: '8px 16px', overflow: 'hidden' }}>
              {rightPanelItems.length === 0 ? (
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px', color: '#999', padding: '12px 0' }}>
                  No amenities loaded. Search for the listing first.
                </div>
              ) : (
                rightPanelItems.map((item, i) => (
                  <RightPanelItem key={i} item={item} listing={listing} />
                ))
              )}
            </div>

            {/* Drive Times panel */}
            <div style={{ padding: '0 16px 12px' }}>
              <DriveTimes listing={listing} />
            </div>
          </div>
        </div>

        {/* ━━━ FOOTER BAR ━━━ */}
        <div style={{
          background: B.offWhite,
          borderTop: `2px solid ${B.border}`,
          padding: '8px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          {/* Branding left */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/images/danielle-logo.png"
              alt="Danielle Edwards"
              style={{ height: '40px', objectFit: 'contain' }}
            />
          </div>

          {/* Disclaimer center */}
          <div style={{ textAlign: 'center', flex: 1, padding: '0 20px' }}>
            <p style={{
              fontFamily: 'Arial, sans-serif', fontSize: '9px',
              color: '#888', margin: 0, lineHeight: 1.4,
            }}>
              All distances &amp; times are approximate per Google Maps. For illustrative purposes only.
              Generated by DRIVEN MAPS — Locally Targeted · DRIVEN ADM System
            </p>
          </div>

          {/* RE/MAX right */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '9px', color: '#888' }}>SoldONDanielle.com</div>
              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '9px', color: '#888', fontWeight: '700' }}>704-604-2999</div>
            </div>
            <img
              src="/images/remax-black.png"
              alt="RE/MAX Executive"
              style={{ height: '34px', objectFit: 'contain' }}
            />
          </div>
        </div>

        {/* ━━━ BOTTOM NAVY BAR ━━━ */}
        <div style={{ height: '5px', background: B.darkNavy, flexShrink: 0 }} />
      </div>

      {/* Scale note */}
      <p className="no-print text-white/40 text-xs mt-3">
        1200 × 900 px · Select PNG for digital, JPG for email/MLS, PDF for print
      </p>
    </div>
  );
}
